import React, { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { NeedsActionHeader } from "../components/NeedsActionHeader";
import { NeedsActionTable } from "../components/NeedsActionTable";
import { NeedsActionPagination } from "../components/NeedsActionPagination";
import { useGetNeedsActionQuery } from "../../infrastructure/api/needsActionApi";
import type {
  NeedsActionSortBy,
  NeedsActionSortOrder,
} from "../../domain/entities/NeedsAction";
import { PageShell } from "@/components/layout/PageShell";

const PAGE_SIZE = 10;

const NeedsAction: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<NeedsActionSortBy>("status_at");
  const [sortOrder, setSortOrder] = useState<NeedsActionSortOrder>("desc");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedQuery, sortBy, sortOrder]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const { data, isLoading, isFetching } = useGetNeedsActionQuery({
    query: debouncedQuery || undefined,
    page: currentPage,
    page_size: PAGE_SIZE,
    sort_by: sortBy,
    sort_order: sortOrder,
  });

  const items = data?.items ?? [];
  const meta = data?.meta;
  const totalItems = meta?.total_count ?? 0;
  const totalPages = meta?.total_pages ?? 1;

  const handleSortChange = (
    newSortBy: NeedsActionSortBy,
    newSortOrder: NeedsActionSortOrder,
  ) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  };

  return (
    <PageShell className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="shrink-0">
        <NeedsActionHeader
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          totalItems={totalItems}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        {isLoading ? (
          <div className="mt-4 flex h-64 items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            <NeedsActionTable items={items} isLoading={isFetching} />
            {totalItems > 0 && (
              <NeedsActionPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={PAGE_SIZE}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>
    </PageShell>
  );
};

export default NeedsAction;
